import { DebugService } from '../shared/services/debug.service.js';
import { stratum } from '../../constants/stratum.js';
import { REFI_PERSONAL_CONST } from '../shared/constants/app.constants.js';

class _ApplicationService {
  constructor() {
    this.debug = new DebugService('ApplicationService');
    this._application = {};
    this._customerOffer = {};
    this._matchingState = '';
    this.primaryEmailChanged = false;
    this.coAppEmailChanged = false;

    // Determining if DOB or SSN have changed since prefill value
    this.primaryDobChanged = false;
    this.coAppDobChanged = false;

    this.primarySsnChanged = false;
    this.coAppSsnChanged = false;

    this._isUpfrontOffers = false;
    this._isIconicAndToggledOn = false;
    this._isIconicV2 = false;
    this._experiments = [];
    this.abTest = null;
    // test name, weight and group are defaulted and should not be used/modified.
    this._abTests = {
      name: 'personal-ab-tests',
      variationIds: [],
      testGroup: '',
      testWeight: 1,
    };
    this._isInNDITest = false;
  }

  get Application() {
    return this._application;
  }

  setApplication(application) {
    this._application = application;
  }

  get CustomerOffer() {
    return this._customerOffer;
  }

  set CustomerOffer(customerOffer) {
    this._customerOffer = customerOffer;
  }

  get experiments() {
    return this._experiments;
  }

  get isIconicAndToggledOn() {
    return this._isIconicAndToggledOn;
  }

  set isIconicAndToggledOn(isIconicAndToggledOn) {
    this._isIconicAndToggledOn = isIconicAndToggledOn;
  }

  get isIconicV2() {
    return this._isIconicV2;
  }

  set isIconicV2(isIconicV2) {
    this._isIconicV2 = isIconicV2;
  }

  updateIconicV2() {
    this.debug.log(
      'updating iconic v2 toggle, this.experiments is ',
      this._experiments
    );
    this._isIconicV2 = this._experiments?.includes(
      REFI_PERSONAL_CONST.ICONIC_V2
    );
  }

  updateExperiments(
    newExperiments,
    removeExperiments = [],
    removeExperimentType = null
  ) {
    stratum.endAbTest(this.abTest);
    const newlyAddedExperiments = newExperiments.map((experiment) =>
      experiment.toLowerCase()
    );
    let updatedExperimentsNoSpecificType = this._experiments;
    if (removeExperimentType) {
      updatedExperimentsNoSpecificType = this._experiments.filter(
        (experiment) =>
          !experiment.toLowerCase().includes(removeExperimentType.toLowerCase())
      );
    }
    const updatedExperiments = updatedExperimentsNoSpecificType.filter(
      (experiment) => !removeExperiments.includes(experiment.toLowerCase())
    );
    this._experiments = Array.from(
      new Set(updatedExperiments.concat(newlyAddedExperiments))
    );
    this._abTests.variationIds = this._experiments;
    this.abTest = stratum.startAbTest(this._abTests);
  }

  get MatchingState() {
    return this._matchingState;
  }

  set MatchingState(matchingState) {
    this._matchingState = matchingState;
  }

  get getPrimaryApplicantEmailChanged() {
    return this.primaryEmailChanged;
  }

  set primaryApplicantEmailChanged(value) {
    this.primaryEmailChanged = value;
  }

  get getCoApplicantEmailChanged() {
    return this.coAppEmailChanged;
  }

  set coApplicantEmailChanged(value) {
    this.coAppEmailChanged = value;
  }

  get getPrimaryApplicantDobChanged() {
    return this.primaryDobChanged;
  }

  set primaryApplicantDobChanged(value) {
    this.primaryDobChanged = value;
  }

  get getCoApplicantDobChanged() {
    return this.coAppDobChanged;
  }

  set coApplicantDobChanged(value) {
    this.coAppDobChanged = value;
  }

  get getPrimaryApplicantSsnChanged() {
    return this.primarySsnChanged;
  }

  set primaryApplicantSsnChanged(value) {
    this.primarySsnChanged = value;
  }

  get getCoApplicantSsnChanged() {
    return this.coAppSsnChanged;
  }

  set coApplicantSsnChanged(value) {
    this.coAppSsnChanged = value;
  }

  get isUpfrontOffers() {
    return this._isUpfrontOffers;
  }

  set isUpfrontOffers(isUpfrontOffers) {
    this._isUpfrontOffers = isUpfrontOffers;
  }

  get isNDITestOn() {
    this.debug.log('NDI test is on: ', this._isInNDITest);
    return this._isInNDITest;
  }

  updateNDITestToggle() {
    this.debug.log(
      'updating NDI test toggle, this.experiments is ',
      this._experiments
    );
    this._isInNDITest = this._experiments?.includes(
      REFI_PERSONAL_CONST.NDI_TEST
    );
  }

  clearCoapplicant() {
    this.Application.coApplicant = JSON.parse(
      JSON.stringify({
        lastName: '',
        firstName: '',
        emailAddress: '',
        confirmEmailAddress: '',
        phoneNumber: '',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '',
          aptNumber: null,
          city: '',
          stateCode: '',
          zipCode: '',
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '',
        employmentStatus: '',
        employmentInfo: {
          employerName: '',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: '',
          activeIncomeVerification: null,
        },
        incomeAmount: null,
        otherIncome: null,
        profileReferenceId: '',
        prospectId: '',
        externalId: '',
        clientCorrelationId: '',
        isSameAddress: null,
        incomeFrequency: '',
        otherIncomeFrequency: '',
      })
    );
  }

  setMockApplication() {
    this._application = {
      loanType: 'REFINANCE',
      applicationType: 'INDIVIDUAL',
      profileType: 'financial',
      primaryApplicant: {
        lastName: 'John',
        firstName: 'Doe',
        emailAddress: 'John.Doe@capitalone.com',
        lastFourTaxId: '1234',
        confirmEmailAddress: '',
        phoneNumber: '606-568-2912',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '113 Pine Meadows',
          aptNumber: '',
          city: 'Dallas',
          stateCode: 'FL',
          zipCode: '75230',
          ownerStatus: 'OWN',
          monthlyPayment: 1000,
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '06/11/1978',
        employmentStatus: 'employed',
        employmentInfo: {
          employerName: 'Capital One',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: 'Software Engineer',
          activeIncomeVerification: null,
        },
        incomeAmount: 80000,
        otherIncome: null,
        prospectId: '',
        clientCorrelationId: '',
        isSameAddress: null,
      },
      coApplicant: {
        lastName: '',
        firstName: '',
        emailAddress: '',
        lastFourTaxId: '',
        confirmEmailAddress: '',
        phoneNumber: '',
        phoneType: 'mobile',
        smsOptIn: false,
        currentAddress: {
          street: '',
          aptNumber: null,
          city: '',
          stateCode: '',
          zipCode: '',
        },
        currentAddressType: 'other',
        ssn: '',
        dateOfBirth: '01/01/2000',
        employmentStatus: '',
        employmentInfo: {
          employerName: '',
          yearsEmployed: null,
          monthsEmployed: null,
          jobDescription: '',
          activeIncome: '',
          activeIncomeVerification: null,
        },
        incomeAmount: null,
        otherIncome: null,
        profileReferenceId: '',
        prospectId: '',
        externalId: '',
        clientCorrelationId: '',
        isSameAddress: null,
      },
      promoCode: '11491700',
      agreeToTerms: null,
      agreeToLookup: null,
      year: '',
      make: '',
      model: '',
      trim: '',
      vin: '',
      wholeSaleValue: '',
      paymentDueDay: null,
      manualEntryFlag: null,
      selectedLoan: null,
    };
  }
}

export const ApplicationService = new _ApplicationService();
